import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck, ShieldQuestion, AlertTriangle } from "lucide-react";

interface Props { range: 'P' | 'R' }

interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; effective_date: string; }
interface SectorMapping { fund_id: string; sector_id: string; weighting: number; }
interface SectorAssetClass { sector_id: string; asset_class: 'equity' | 'credit' | 'other' }
interface PolicyRange { profile_level: number; asset_class: string; min_pct: number; max_pct: number }
interface PermittedInstrument { instrument_type: string; permitted: boolean; conditions: string | null }

// risk_sector_asset_class enum → policy display name. 'other' is treated as Alternatives;
// Cash (uninvested) has no sector mapping so it's reported as the residual.
const ASSET_CLASS_DISPLAY: Record<string, string> = {
  equity: 'Equity',
  credit: 'Fixed Income',
  other: 'Alternatives',
};

const PORTFOLIOS = [1, 2, 3, 4, 5];
// Money Market holdings are treated as Cash for §6 asset-class range compliance.
const MONEY_MARKET_SECTOR_ID = "37dadcc2-3816-4c60-8614-86420bdf8f96";

export default function PolicyCompliancePanel({ range }: Props) {
  const weightsTable = range === 'P' ? 'ba_cbam_fund_weights' : 'ba_r_cbam_fund_weights';

  const { data: weights = [] } = useQuery({
    queryKey: [`policy-compliance-weights`, range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(weightsTable as any)
        .select('fund_id, portfolio, weight_pct, effective_date');
      if (error) throw error;
      const latest = new Map<string, FundWeight>();
      for (const w of (data as any) as FundWeight[]) {
        const key = `${w.fund_id}-${w.portfolio}`;
        const prev = latest.get(key);
        if (!prev || w.effective_date > prev.effective_date) latest.set(key, w);
      }
      return Array.from(latest.values());
    },
  });

  const { data: mappings = [] } = useQuery({
    queryKey: ['policy-compliance-sector-mappings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_cbam_fund_sector_mappings')
        .select('fund_id, sector_id, weighting');
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: sectorAC = [] } = useQuery({
    queryKey: ['risk-sector-asset-class'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_sector_asset_class' as any)
        .select('sector_id, asset_class');
      if (error) throw error;
      return (data ?? []) as unknown as SectorAssetClass[];
    },
  });

  const { data: ranges = [] } = useQuery({
    queryKey: ['policy-asset-class-ranges'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policy_asset_class_ranges' as any)
        .select('profile_level, asset_class, min_pct, max_pct')
        .order('profile_level');
      if (error) throw error;
      return (data ?? []) as unknown as PolicyRange[];
    },
  });

  const { data: instruments = [] } = useQuery({
    queryKey: ['policy-permitted-instruments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policy_permitted_instruments' as any)
        .select('instrument_type, permitted, conditions')
        .order('instrument_type');
      if (error) throw error;
      return (data ?? []) as unknown as PermittedInstrument[];
    },
  });

  const acBySectorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of sectorAC) m.set(r.sector_id, r.asset_class);
    return m;
  }, [sectorAC]);

  // For each portfolio level, compute % allocation per Equity / Fixed Income / Alternatives / Cash
  const allocations = useMemo(() => {
    const result = new Map<number, Record<string, number>>();
    for (const p of PORTFOLIOS) {
      result.set(p, { Equity: 0, 'Fixed Income': 0, Alternatives: 0, Cash: 0, Unmapped: 0 });
    }
    for (const w of weights) {
      if (Number(w.weight_pct) <= 0) continue;
      const fundMaps = mappings.filter(m => m.fund_id === w.fund_id);
      if (fundMaps.length === 0) {
        const bucket = result.get(w.portfolio)!;
        bucket.Unmapped += Number(w.weight_pct);
        continue;
      }
      for (const m of fundMaps) {
        const contribution = Number(w.weight_pct) * Number(m.weighting);
        const bucket = result.get(w.portfolio)!;
        if (m.sector_id === MONEY_MARKET_SECTOR_ID) {
          bucket.Cash = (bucket.Cash ?? 0) + contribution;
          continue;
        }
        const ac = acBySectorId.get(m.sector_id);
        const display = ac ? ASSET_CLASS_DISPLAY[ac] ?? 'Alternatives' : 'Unmapped';
        bucket[display] = (bucket[display] ?? 0) + contribution;
      }
    }
    // Cash = explicit Money Market holdings + residual to 100 if total still < 100
    for (const p of PORTFOLIOS) {
      const b = result.get(p)!;
      const total = b.Equity + b['Fixed Income'] + b.Alternatives + b.Cash + b.Unmapped;
      if (total < 99.5) b.Cash += 100 - total;
    }
    return result;
  }, [weights, mappings, acBySectorId]);

  const rangesByLevel = useMemo(() => {
    const m = new Map<number, Map<string, PolicyRange>>();
    for (const r of ranges) {
      if (!m.has(r.profile_level)) m.set(r.profile_level, new Map());
      m.get(r.profile_level)!.set(r.asset_class, r);
    }
    return m;
  }, [ranges]);

  function status(value: number, range: PolicyRange | undefined): 'within' | 'amber' | 'breach' | 'none' {
    if (!range) return 'none';
    if (value < range.min_pct || value > range.max_pct) return 'breach';
    const min = Number(range.min_pct), max = Number(range.max_pct);
    const buffer = (max - min) * 0.05;
    if (value < min + buffer || value > max - buffer) return 'amber';
    return 'within';
  }

  const ASSET_CLASSES = ['Equity', 'Fixed Income', 'Alternatives', 'Cash'];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Policy Compliance — Asset-Class Ranges (Tier 1 §6)</CardTitle>
          <CardDescription>
            Live asset-class allocations per {range} portfolio against §6 permitted ranges. Equity/Credit derived from CBAM sector → asset-class mapping; Cash imputed as residual to 100%. Edit ranges in Compliance → Thresholds.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio</TableHead>
                {ASSET_CLASSES.map(ac => (
                  <TableHead key={ac} className="text-right">{ac} %</TableHead>
                ))}
                <TableHead className="text-right">Unmapped %</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PORTFOLIOS.map(p => {
                const alloc = allocations.get(p)!;
                const lvlRanges = rangesByLevel.get(p);
                const cellStatuses = ASSET_CLASSES.map(ac => ({
                  ac,
                  value: alloc[ac] ?? 0,
                  range: lvlRanges?.get(ac),
                  status: status(alloc[ac] ?? 0, lvlRanges?.get(ac)),
                }));
                const worst: 'within' | 'amber' | 'breach' | 'none' = cellStatuses.reduce<'within' | 'amber' | 'breach' | 'none'>((acc, c) => {
                  if (c.status === 'breach') return 'breach';
                  if (c.status === 'amber' && acc !== 'breach') return 'amber';
                  if (c.status === 'none' && acc === 'within') return 'none';
                  return acc;
                }, 'within');
                return (
                  <TableRow key={p}>
                    <TableCell className="font-medium">{range}{p}</TableCell>
                    {cellStatuses.map(c => (
                      <TableCell key={c.ac} className="text-right tabular-nums">
                        <div className="flex flex-col items-end">
                          <span className={
                            c.status === 'breach' ? 'text-destructive font-semibold'
                            : c.status === 'amber' ? 'text-amber-600 font-medium'
                            : ''
                          }>{c.value.toFixed(1)}</span>
                          {c.range && (
                            <span className="text-[10px] text-muted-foreground">
                              {Number(c.range.min_pct).toFixed(0)}–{Number(c.range.max_pct).toFixed(0)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {alloc.Unmapped > 0.05 ? alloc.Unmapped.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell><RangeStatusBadge status={worst} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {weights.length === 0 && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-2">
              <AlertTriangle className="h-3 w-3" /> No fund weights found for {range} portfolios.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Permitted Instruments (Tier 1 §7)</CardTitle>
          <CardDescription>Reference list of instrument types and conditions. Edit in Compliance → Thresholds.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instrument Type</TableHead>
                <TableHead>Permitted</TableHead>
                <TableHead>Conditions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instruments.map(i => (
                <TableRow key={i.instrument_type}>
                  <TableCell className="font-medium text-sm">{i.instrument_type}</TableCell>
                  <TableCell>
                    {i.permitted
                      ? <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Permitted</Badge>
                      : <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Not Permitted</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{i.conditions ?? '—'}</TableCell>
                </TableRow>
              ))}
              {instruments.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground text-sm py-6">No instruments configured.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function RangeStatusBadge({ status }: { status: 'within' | 'amber' | 'breach' | 'none' }) {
  if (status === 'breach') return <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Breach</Badge>;
  if (status === 'amber') return <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-600"><ShieldAlert className="h-3 w-3" />Near limit</Badge>;
  if (status === 'none') return <Badge variant="outline" className="gap-1"><ShieldQuestion className="h-3 w-3" />No policy</Badge>;
  return <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Within</Badge>;
}
